import React, { useState, useRef, useCallback, useEffect } from "react";
import "./ai-chatbot.css";
import apiService from "../../api/apiService";
import tiger0 from "../../images/tiger0.png";
import renderMarkdown, { markdownToPlain } from "./renderMarkdown";

// The assistant picks its own data sources; these only seed a new chat.
const PROMPT_CATEGORIES = [
  {
    id: "brief",
    label: "Brief",
    icon: "🧭",
    prompts: [
      { icon: "☀️", title: "Today's Brief", prompt: "Give me today's operations brief: orders, deliveries, overdue BuildCredit, pending PRs, fleet issues, and chat issues." },
      { icon: "📊", title: "Week vs Last Week", prompt: "What changed this week compared to last week across orders, collections and PRs?" },
      { icon: "⚠️", title: "Top Risks", prompt: "What are the top 3 risks I should know about right now?" },
      { icon: "🗓️", title: "Yesterday", prompt: "Summarize what happened yesterday across the business." },
    ],
  },
  {
    id: "sales",
    label: "Sales",
    icon: "📦",
    prompts: [
      { icon: "📦", title: "Orders This Month", prompt: "How many orders did we get this month, and how does the total value compare with last month?" },
      { icon: "🏆", title: "Top Customers", prompt: "Who are our top 10 customers by sales this quarter?" },
      { icon: "🤝", title: "Top Agents", prompt: "Which agents brought in the most sales this month?" },
      { icon: "🔥", title: "Best Sellers", prompt: "What are our best-selling products this month?" },
      { icon: "💤", title: "Inactive Customers", prompt: "Which active customers haven't ordered in the last 60 days?" },
      { icon: "🆕", title: "New Customers", prompt: "How many new customers signed up this month?" },
    ],
  },
  {
    id: "buildcredit",
    label: "BuildCredit",
    icon: "💳",
    prompts: [
      { icon: "💳", title: "Outstanding & Overdue", prompt: "What is our total outstanding and overdue BuildCredit right now?" },
      { icon: "🚨", title: "Biggest Overdue", prompt: "Which customers have the biggest overdue BuildCredit balance?" },
      { icon: "📅", title: "Due This Week", prompt: "Which BuildCredit accounts are due this week?" },
      { icon: "💰", title: "Collections", prompt: "How much in BuildCredit payments did we collect this month?" },
      { icon: "📝", title: "Pending Applications", prompt: "How many BuildCredit applications are waiting for approval?" },
      { icon: "🤞", title: "Broken Promises", prompt: "Which accounts have broken payment promises?" },
    ],
  },
  {
    id: "procurement",
    label: "Procurement",
    icon: "🚚",
    prompts: [
      { icon: "⏳", title: "Pending PR Cost", prompt: "What is the total cost of all pending PRs this month?" },
      { icon: "🔴", title: "Urgent PRs", prompt: "Which urgent PRs are still pending?" },
      { icon: "🚚", title: "Open Supplier POs", prompt: "Which supplier purchase orders are still not fully received?" },
      { icon: "✅", title: "POs for Approval", prompt: "Which purchase orders are waiting for approval?" },
      { icon: "🔁", title: "Transfers in Transit", prompt: "Which inventory transfers are still in transit?" },
    ],
  },
  {
    id: "fleet",
    label: "Fleet",
    icon: "🚛",
    isNew: true,
    prompts: [
      { icon: "🛠️", title: "Fleet Status", prompt: "Which trucks, forklifts and vessels are Under Maintenance or For Repair right now?" },
      { icon: "🚨", title: "Major Truck Issues", prompt: "Which trucks had the most major issues in PM inspections this month, and on which checks?" },
      { icon: "⛽", title: "Fuel & Tolls", prompt: "How much did we spend on truck fuel and tolls this month compared with last month?" },
      { icon: "🏗️", title: "Forklift Defects", prompt: "Which forklifts had defects in their latest PM inspection?" },
      { icon: "🚢", title: "Vessel Fuel", prompt: "How many litres of fuel did each vessel use this month?" },
      { icon: "📋", title: "Missing Fuel Logs", prompt: "Which trucks did not submit a fuel log yesterday?" },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    icon: "💬",
    prompts: [
      { icon: "💬", title: "Reported Issues", prompt: "What problems or delays were reported in group chats this week?" },
      { icon: "📈", title: "Busiest Chats", prompt: "Which group chats were most active today?" },
      { icon: "🚛", title: "Delivery Complaints", prompt: "Are there recurring complaints about deliveries this month?" },
      { icon: "🧩", title: "Unresolved Issues", prompt: "Which issues were raised but not resolved this week?" },
    ],
  },
  {
    id: "market",
    label: "Market",
    icon: "🌐",
    prompts: [
      { icon: "🌐", title: "Price Check", prompt: "How do our cement and steel bar prices compare with major hardware stores right now?" },
      { icon: "📰", title: "Market News", prompt: "What's happening in the Philippine construction market this week?" },
      { icon: "📈", title: "Price Increases", prompt: "Have any price increases been announced for cement, steel or fuel?" },
      { icon: "🏗️", title: "Gov't Projects", prompt: "Are there new government infrastructure projects or policies that affect us?" },
    ],
  },
];

const PROMPT_TAB_KEY = "tigerx-ai-prompt-tab";

// Chats from before the assistant chose its own sources still carry a domain.
const LEGACY_DOMAIN_LABELS = { messages: "Messages", pr: "PR" };

const GROUP_ORDER = ["Pinned", "Today", "Yesterday", "Previous 7 days", "Older"];

// Within this many px of the bottom, new text keeps the thread pinned to the end.
const STICK_TO_BOTTOM_PX = 80;

const generateId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2);

const titleFromQuestion = (question) => {
  const clean = question.replace(/\s+/g, " ").trim();
  if (clean.length <= 80) return clean || "New chat";
  return `${clean.slice(0, 77).trimEnd()}...`;
};

const conversationTime = (item) => new Date(item.lastMessageAt || item.createdAt || 0);

const sortConversations = (list) =>
  [...list].sort((a, b) => {
    if (!!a.pinned !== !!b.pinned) return a.pinned ? -1 : 1;
    return conversationTime(b) - conversationTime(a);
  });

const groupConversations = (list) => {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startWeek = new Date(startToday);
  startWeek.setDate(startWeek.getDate() - 7);

  const groups = {
    Pinned: [],
    Today: [],
    Yesterday: [],
    "Previous 7 days": [],
    Older: [],
  };

  list.forEach((item) => {
    if (item.pinned) {
      groups.Pinned.push(item);
      return;
    }
    const time = conversationTime(item);
    if (time >= startToday) groups.Today.push(item);
    else if (time >= startYesterday) groups.Yesterday.push(item);
    else if (time >= startWeek) groups["Previous 7 days"].push(item);
    else groups.Older.push(item);
  });

  return GROUP_ORDER.filter((name) => groups[name].length).map((name) => ({
    name,
    items: groups[name],
  }));
};

const formatListTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  if (date >= startToday) {
    return date.toLocaleTimeString("en-PH", { hour: "numeric", minute: "2-digit" });
  }
  if (date >= startYesterday) return "Yesterday";
  return date.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
};

// Server rows → bubbles. Each AI bubble remembers the question it answered, for Retry.
const mapServerMessages = (rows) => {
  let lastQuestion = "";
  return (rows || []).map((msg) => {
    const isAi = msg.role === "assistant";
    if (!isAi) lastQuestion = msg.content || "";
    return {
      id: msg.id,
      serverId: isAi ? msg.id : null,
      role: isAi ? "ai" : "user",
      content: msg.content || "",
      ts: new Date(msg.createdAt),
      question: isAi ? lastQuestion : undefined,
      sources: msg.meta?.sources || null,
      followUps: msg.meta?.followUps || [],
      feedback: msg.feedback ?? null,
      feedbackNote: msg.feedbackNote || "",
    };
  });
};

const friendlyError = (data) => {
  if (data?.status === 401) return "Your session has expired. Please sign in again.";
  if (data?.status === 503) return "Tiger X AI isn't available right now. Please contact the system administrator.";
  return data?.message || "Something went wrong. Please try again.";
};

const readPromptTab = () => {
  try {
    const saved = localStorage.getItem(PROMPT_TAB_KEY);
    return PROMPT_CATEGORIES.some((c) => c.id === saved) ? saved : "brief";
  } catch {
    return "brief";
  }
};

function SourcesRow({ sources }) {
  const [open, setOpen] = useState(false);
  const internal = sources?.internal || [];
  const web = sources?.web || [];
  if (!internal.length && !web.length) return null;
  const summary = [...internal, ...(web.length ? [`Web (${web.length})`] : [])].join(" · ");
  return (
    <div className="ai-chatbot__sources">
      <button
        type="button"
        className="ai-chatbot__sources-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span>Checked: {summary}</span>
        <svg className={`ai-chatbot__chev${open ? " ai-chatbot__chev--open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="ai-chatbot__sources-list">
          {internal.length > 0 && (
            <div>
              <span className="ai-chatbot__sources-label">Buildhub data</span>
              <span>{internal.join(", ")}</span>
            </div>
          )}
          {web.length > 0 && (
            <div>
              <span className="ai-chatbot__sources-label">Web</span>
              <ul>
                {web.map((w) => (
                  <li key={w.url}>
                    <a href={w.url} target="_blank" rel="noopener noreferrer">
                      {w.title || w.url}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AiChatbot({ onClose, userDetails }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [steps, setSteps] = useState([]);
  const [streamingId, setStreamingId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [search, setSearch] = useState("");
  const [railOpen, setRailOpen] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth > 900 : true,
  );
  const [activeId, setActiveId] = useState(null);
  const [activeMeta, setActiveMeta] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [promptTab, setPromptTab] = useState(readPromptTab);
  const [copiedId, setCopiedId] = useState(null);
  const [noteDraft, setNoteDraft] = useState(null); // { id, text }
  const [showJump, setShowJump] = useState(false);

  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const stickRef = useRef(true);
  const inputRef = useRef(null);
  const turnRef = useRef(0);
  const activeIdRef = useRef(null);
  const restoredRef = useRef(false);
  const renameInputRef = useRef(null);
  const renameLockRef = useRef(false);
  const conversationsRef = useRef([]);
  const abortRef = useRef(null);

  conversationsRef.current = conversations;

  const userId = userDetails?.id || "";
  const storageKey = userId ? `tigerx-ai-thread:${userId}` : "";

  const rememberThread = useCallback(
    (id) => {
      if (storageKey && id) localStorage.setItem(storageKey, id);
    },
    [storageKey],
  );

  const forgetThread = useCallback(
    (id) => {
      if (storageKey && localStorage.getItem(storageKey) === id) {
        localStorage.removeItem(storageKey);
      }
    },
    [storageKey],
  );

  const upsertConversation = useCallback((conversation) => {
    if (!conversation?.id) return;
    setConversations((prev) => {
      const without = prev.filter((item) => item.id !== conversation.id);
      return sortConversations([conversation, ...without]);
    });
    if (activeIdRef.current === conversation.id) setActiveMeta(conversation);
  }, []);

  // Switching threads leaves an in-flight answer running so it still gets saved.
  const beginThreadSwitch = useCallback(() => {
    turnRef.current += 1;
    abortRef.current = null;
    setIsTyping(false);
    setSteps([]);
    setStreamingId(null);
    setNoteDraft(null);
    stickRef.current = true;
    setShowJump(false);
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    stickRef.current = true;
    setShowJump(false);
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "end" });
  }, []);

  const handleThreadScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
    stickRef.current = atBottom;
    if (atBottom) setShowJump(false);
  };

  // Follow new text only while the reader is at the bottom; otherwise offer a jump pill.
  useEffect(() => {
    if (stickRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    } else if (messages.length) {
      setShowJump(true);
    }
  }, [messages, steps, threadLoading]);

  useEffect(() => {
    if (renameId) renameInputRef.current?.focus();
  }, [renameId]);

  useEffect(() => {
    try {
      localStorage.setItem(PROMPT_TAB_KEY, promptTab);
    } catch {}
  }, [promptTab]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const loadConversations = useCallback(async (term = "") => {
    const result = await apiService.talk_to_ai.listConversations(term);
    if (result?.success) {
      setConversations(result.conversations || []);
      setListError("");
      return result.conversations || [];
    }
    setListError("Couldn't load chat history.");
    return null;
  }, []);

  const openConversation = useCallback(
    async (id, { closeRail = false } = {}) => {
      if (!id) return;
      beginThreadSwitch();
      const turn = turnRef.current;
      activeIdRef.current = id;
      setActiveId(id);
      setActiveMeta(conversationsRef.current.find((item) => item.id === id) || null);
      setThreadLoading(true);
      setThreadError("");
      setMessages([]);
      setInputValue("");
      setRenameId(null);
      if (closeRail && window.innerWidth <= 900) setRailOpen(false);

      const result = await apiService.talk_to_ai.getConversation(id);
      if (turnRef.current !== turn || activeIdRef.current !== id) return;

      if (!result?.success) {
        setThreadLoading(false);
        setThreadError(result?.message || "Couldn't open this chat.");
        if (result?.message === "Conversation not found") forgetThread(id);
        return;
      }

      setMessages(mapServerMessages(result.messages));
      upsertConversation(result.conversation);
      rememberThread(id);
      setThreadLoading(false);
      inputRef.current?.focus();
    },
    [beginThreadSwitch, forgetThread, rememberThread, upsertConversation],
  );

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(async () => {
      const items = await loadConversations(search);
      if (cancelled) return;
      setListLoading(false);
      if (!restoredRef.current && !search && items && userId) {
        restoredRef.current = true;
        const saved = storageKey ? localStorage.getItem(storageKey) : "";
        if (saved && items.some((item) => item.id === saved)) {
          openConversation(saved);
        }
      }
    }, search ? 250 : 0);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search, storageKey, userId, loadConversations, openConversation]);

  const updateMessage = useCallback((match, patch) => {
    setMessages((prev) => prev.map((m) => (match(m) ? { ...m, ...patch } : m)));
  }, []);

  const sendMessage = useCallback(
    async (text) => {
      const trimmed = text.trim();
      if (!trimmed || isTyping) return;

      const turn = turnRef.current;
      const userMsgId = generateId();
      const aiMsgId = generateId();
      const controller = new AbortController();
      abortRef.current = controller;
      setMessages((prev) => [
        ...prev,
        { id: userMsgId, role: "user", content: trimmed, ts: new Date() },
      ]);
      setIsTyping(true);
      setSteps(["Thinking…"]);
      setThreadError("");
      setNoteDraft(null);
      scrollToBottom();

      let conversationId = activeIdRef.current;
      let answerText = "";
      let settled = false;

      // Stale events (user switched threads mid-answer) are ignored.
      const isCurrent = () =>
        turnRef.current === turn &&
        (!conversationId || activeIdRef.current === conversationId);

      const setAi = (patch) => {
        setMessages((prev) =>
          prev.some((m) => m.id === aiMsgId)
            ? prev.map((m) => (m.id === aiMsgId ? { ...m, ...patch } : m))
            : [
                ...prev,
                { id: aiMsgId, role: "ai", content: "", ts: new Date(), question: trimmed, followUps: [], ...patch },
              ],
        );
      };

      // Runs once per question: on `done` (follow-ups may still stream in after it)
      // or when the stream ends. A newer question owns the typing state after that.
      let finished = false;
      const finishTyping = () => {
        if (finished) return;
        finished = true;
        if (turnRef.current !== turn) return;
        if (abortRef.current && abortRef.current !== controller) return;
        setIsTyping(false);
        setSteps([]);
        setStreamingId(null);
        abortRef.current = null;
      };

      const handleEvent = (event, data) => {
        if (event === "conversation") {
          if (turnRef.current !== turn) return;
          if (!conversationId && data?.id) {
            conversationId = data.id;
            activeIdRef.current = conversationId;
            setActiveId(conversationId);
            rememberThread(conversationId);
          }
          upsertConversation({
            ...data,
            title:
              !data.title || data.title === "New chat"
                ? titleFromQuestion(trimmed)
                : data.title,
            preview: trimmed.slice(0, 500),
            lastMessageAt: new Date().toISOString(),
          });
          return;
        }
        if (event === "done" && data?.conversation) upsertConversation(data.conversation);
        if (!isCurrent()) return;

        if (event === "status") {
          const step = data?.text || "";
          if (step) setSteps((prev) => (prev[prev.length - 1] === step ? prev : [...prev, step]));
        } else if (event === "delta") {
          answerText += data?.text || "";
          setAi({ content: answerText });
          setStreamingId(aiMsgId);
        } else if (event === "reset") {
          answerText = "";
          setMessages((prev) => prev.filter((m) => m.id !== aiMsgId));
          setStreamingId(null);
        } else if (event === "sources") {
          setAi({ sources: data });
        } else if (event === "done") {
          settled = true;
          setAi({
            content: data?.answer || answerText || "Sorry, I couldn't get a response.",
            serverId: data?.messageId || null,
            sources: data?.sources || null,
          });
          // The answer is complete; follow-up chips may still arrive on this stream.
          finishTyping();
        } else if (event === "followups") {
          if (Array.isArray(data?.items)) setAi({ followUps: data.items.slice(0, 3) });
        } else if (event === "error") {
          settled = true;
          setAi({ content: friendlyError(data), status: "error" });
        }
      };

      try {
        await apiService.talk_to_ai.streamQuery(
          conversationId ? { question: trimmed, conversationId } : { question: trimmed },
          handleEvent,
          controller.signal,
        );
        if (isCurrent() && !settled) {
          setAi({
            content: answerText || "The connection was interrupted. Please try again.",
            status: answerText ? "stopped" : "error",
          });
        }
      } catch (err) {
        if (!isCurrent()) return;
        if (controller.signal.aborted) {
          if (!settled) setAi({ content: answerText, status: "stopped" });
        } else {
          setAi({ content: err?.message || "Something went wrong. Please try again.", status: "error" });
        }
      } finally {
        finishTyping();
      }
    },
    [isTyping, rememberThread, upsertConversation, scrollToBottom],
  );

  const stopAnswer = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleNewChat = useCallback(() => {
    beginThreadSwitch();
    activeIdRef.current = null;
    setActiveId(null);
    setActiveMeta(null);
    setMessages([]);
    setInputValue("");
    setThreadError("");
    setThreadLoading(false);
    setRenameId(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [beginThreadSwitch]);

  // Esc stops an answer, Ctrl/⌘+K starts a new chat, "/" jumps to the input.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      const typing = tag === "input" || tag === "textarea" || e.target?.isContentEditable;
      if (e.key === "Escape" && abortRef.current) {
        e.preventDefault();
        stopAnswer();
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        handleNewChat();
      } else if (e.key === "/" && !typing) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stopAnswer, handleNewChat]);

  const handleSend = () => {
    const trimmed = inputValue.trim();
    if (!trimmed || isTyping) return;
    sendMessage(trimmed);
    setInputValue("");
    if (inputRef.current) inputRef.current.style.height = "auto";
  };

  const handlePromptClick = (prompt) => sendMessage(prompt);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const copyAnswer = async (msg) => {
    try {
      await navigator.clipboard.writeText(markdownToPlain(msg.content));
      setCopiedId(msg.id);
      setTimeout(() => setCopiedId((id) => (id === msg.id ? null : id)), 1500);
    } catch {
      setThreadError("Couldn't copy to the clipboard.");
    }
  };

  const rateAnswer = async (msg, rating) => {
    if (!msg.serverId) return;
    const next = msg.feedback === rating ? null : rating;
    const previous = { feedback: msg.feedback ?? null, feedbackNote: msg.feedbackNote || "" };
    updateMessage((m) => m.id === msg.id, { feedback: next });
    setNoteDraft(next === -1 ? { id: msg.id, text: msg.feedbackNote || "" } : null);
    const result = await apiService.talk_to_ai.rateMessage(msg.serverId, next);
    if (!result?.success) {
      updateMessage((m) => m.id === msg.id, previous);
      setThreadError(result?.message || "Couldn't save your feedback.");
    }
  };

  const submitNote = async (msg) => {
    const text = (noteDraft?.text || "").trim();
    setNoteDraft(null);
    if (!text || !msg.serverId) return;
    updateMessage((m) => m.id === msg.id, { feedbackNote: text });
    const result = await apiService.talk_to_ai.rateMessage(msg.serverId, -1, text);
    if (!result?.success) setThreadError(result?.message || "Couldn't save your feedback.");
  };

  const startRename = (event, item) => {
    event.stopPropagation();
    setRenameId(item.id);
    setRenameValue(item.title || "");
  };

  const cancelRename = () => {
    setRenameId(null);
    setRenameValue("");
  };

  const commitRename = async (id) => {
    if (renameLockRef.current) return;
    const nextTitle = renameValue.replace(/\s+/g, " ").trim();
    const current = conversations.find((item) => item.id === id);
    if (!nextTitle || nextTitle === current?.title) {
      cancelRename();
      return;
    }
    renameLockRef.current = true;
    const result = await apiService.talk_to_ai.updateConversation(id, { title: nextTitle });
    renameLockRef.current = false;
    if (result?.success && result.conversation) {
      upsertConversation(result.conversation);
      cancelRename();
      return;
    }
    setThreadError(result?.message || "Couldn't rename this chat.");
  };

  const togglePin = async (event, item) => {
    event.stopPropagation();
    const result = await apiService.talk_to_ai.updateConversation(item.id, {
      pinned: !item.pinned,
    });
    if (result?.success && result.conversation) {
      upsertConversation(result.conversation);
      return;
    }
    setListError(result?.message || "Couldn't update this chat.");
  };

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const result = await apiService.talk_to_ai.deleteConversation(deleteTarget.id);
    setDeleting(false);
    if (!result?.success) {
      setListError(result?.message || "Couldn't delete this chat.");
      return;
    }
    setConversations((prev) => prev.filter((item) => item.id !== deleteTarget.id));
    forgetThread(deleteTarget.id);
    if (activeIdRef.current === deleteTarget.id) handleNewChat();
    setDeleteTarget(null);
  };

  const formatTime = (date) => {
    const value = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(value.getTime())) return "";
    return value.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" });
  };

  const grouped = groupConversations(conversations);
  const headerSubtitle = activeId && activeMeta?.title
    ? activeMeta.title
    : "Admin Assistant";
  const activeCategory =
    PROMPT_CATEGORIES.find((c) => c.id === promptTab) || PROMPT_CATEGORIES[0];
  const lastAiId = [...messages].reverse().find((m) => m.role === "ai")?.id;

  return (
    <div className="ai-chatbot-panel">
      <div className="ai-chatbot__header">
        <button
          className={`ai-chatbot__action-btn${railOpen ? " ai-chatbot__action-btn--on" : ""}`}
          onClick={() => setRailOpen((open) => !open)}
          title={railOpen ? "Hide chat history" : "Show chat history"}
          aria-pressed={railOpen}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <div className="ai-chatbot__header-bot-icon">
          <img src={tiger0} alt="Tiger X AI" className="sidebar-header__nav-icon" />
        </div>
        <div className="ai-chatbot__header-info">
          <span className="ai-chatbot__header-title">Tiger X AI</span>
          <span className="ai-chatbot__header-subtitle">{headerSubtitle}</span>
        </div>

        <div className="ai-chatbot__header-actions">
          <button className="ai-chatbot__new-chat-btn" onClick={handleNewChat} title="New chat">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
            </svg>
            <span>New</span>
          </button>
          <button className="ai-chatbot__action-btn ai-chatbot__close-btn" onClick={onClose} title="Close">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      <div className="ai-chatbot__body">
        {railOpen && (
          <button
            className="ai-chatbot__history-backdrop"
            onClick={() => setRailOpen(false)}
            aria-label="Close chat history"
          />
        )}

        <aside className={`ai-chatbot__history${railOpen ? "" : " ai-chatbot__history--collapsed"}`}>
          <div className="ai-chatbot__history-top">
            <button className="ai-chatbot__history-new" onClick={handleNewChat}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              New chat
            </button>
            <div className="ai-chatbot__history-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                <path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <input
                value={search}
                onChange={(e) => {
                  setListLoading(false);
                  setSearch(e.target.value);
                }}
                placeholder="Search chats"
                aria-label="Search chats"
              />
            </div>
          </div>

          <div className="ai-chatbot__history-list">
            {listLoading ? (
              <div className="ai-chatbot__history-skeletons">
                {Array.from({ length: 6 }).map((_, index) => (
                  <div key={index} className="ai-chatbot__history-skeleton" />
                ))}
              </div>
            ) : listError ? (
              <div className="ai-chatbot__history-empty">
                <p>{listError}</p>
                <button type="button" onClick={() => loadConversations(search)}>
                  Try again
                </button>
              </div>
            ) : grouped.length === 0 ? (
              <div className="ai-chatbot__history-empty">
                <p>
                  {search
                    ? "No chats match your search."
                    : "No chats yet. Start a topic and it will stay here."}
                </p>
              </div>
            ) : (
              grouped.map((group) => (
                <section key={group.name} className="ai-chatbot__history-group">
                  <h3>{group.name}</h3>
                  {group.items.map((item) => {
                    const legacyLabel = LEGACY_DOMAIN_LABELS[item.domain];
                    const isActive = item.id === activeId;
                    return (
                      <div
                        key={item.id}
                        className={`ai-chatbot__history-item${isActive ? " ai-chatbot__history-item--active" : ""}`}
                      >
                        {renameId === item.id ? (
                          <form
                            className="ai-chatbot__rename-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              commitRename(item.id);
                            }}
                          >
                            <input
                              ref={renameInputRef}
                              value={renameValue}
                              maxLength={120}
                              onChange={(event) => setRenameValue(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Escape") cancelRename();
                              }}
                              onBlur={() => commitRename(item.id)}
                              aria-label="Chat title"
                            />
                          </form>
                        ) : (
                          <button
                            type="button"
                            className="ai-chatbot__history-open"
                            onClick={() => openConversation(item.id, { closeRail: true })}
                          >
                            <span className="ai-chatbot__history-title-row">
                              <span className="ai-chatbot__history-title">{item.title || "New chat"}</span>
                              <span className="ai-chatbot__history-time">
                                {formatListTime(item.lastMessageAt || item.createdAt)}
                              </span>
                            </span>
                            {(legacyLabel || item.pinned) && (
                              <span className="ai-chatbot__history-meta">
                                {legacyLabel && (
                                  <span className={`ai-chatbot__mini-chip ai-chatbot__mini-chip--${item.domain}`}>
                                    {legacyLabel}
                                  </span>
                                )}
                                {item.pinned && <span className="ai-chatbot__pin-dot">Pinned</span>}
                              </span>
                            )}
                            {item.preview && (
                              <span className="ai-chatbot__history-preview">{item.preview}</span>
                            )}
                          </button>
                        )}
                        {renameId !== item.id && (
                          <div className="ai-chatbot__history-actions">
                            <button type="button" title={item.pinned ? "Unpin" : "Pin"} onClick={(event) => togglePin(event, item)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill={item.pinned ? "currentColor" : "none"}>
                                <path d="M14 3l7 7-4 1-4 8-2-2-5 5 1-6-2-2 8-4 1-7z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button type="button" title="Rename" onClick={(event) => startRename(event, item)}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M4 20h4l10-10-4-4L4 16v4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                              </svg>
                            </button>
                            <button type="button" title="Delete" onClick={(event) => { event.stopPropagation(); setDeleteTarget(item); }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <path d="M4 7h16M9 7V5h6v2M8 7l1 13h6l1-13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </section>
              ))
            )}
          </div>
        </aside>

        <div className="ai-chatbot__thread">
          <div
            className="ai-chatbot__messages"
            ref={scrollRef}
            onScroll={handleThreadScroll}
          >
            {threadError && (
              <div className="ai-chatbot__thread-error" role="alert">{threadError}</div>
            )}
            {threadLoading ? (
              <div className="ai-chatbot__thread-skeletons">
                <div className="ai-chatbot__thread-skeleton ai-chatbot__thread-skeleton--user" />
                <div className="ai-chatbot__thread-skeleton" />
                <div className="ai-chatbot__thread-skeleton ai-chatbot__thread-skeleton--short" />
              </div>
            ) : messages.length === 0 ? (
              <div className="ai-chatbot__welcome">
                <div className="ai-chatbot__welcome-icon-wrap">
                  <div className="ai-chatbot__welcome-icon-ring" aria-hidden="true" />
                  <img src={tiger0} alt="" className="ai-chatbot__welcome-icon" />
                </div>
                <h2 className="ai-chatbot__welcome-title">Tiger X AI Assistant</h2>
                <p className="ai-chatbot__welcome-subtitle">
                  Ask about sales, customers, BuildCredit, PRs and POs, inventory, team chats, the market,
                  and now the <strong>fleet</strong>: trucks, forklifts, vessels, PM inspections and fuel.
                </p>
                {activeCategory.id !== "fleet" && (
                  <button
                    type="button"
                    className="ai-chatbot__new-source"
                    onClick={() => setPromptTab("fleet")}
                  >
                    <span className="ai-chatbot__new-badge">New</span>
                    Fleet data is now connected
                    <span className="ai-chatbot__new-source-arrow" aria-hidden="true">→</span>
                  </button>
                )}
                <div className="ai-chatbot__prompt-tabs" role="tablist" aria-label="Suggested topics">
                  {PROMPT_CATEGORIES.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      role="tab"
                      aria-selected={category.id === activeCategory.id}
                      className={`ai-chatbot__prompt-tab${category.id === activeCategory.id ? " ai-chatbot__prompt-tab--on" : ""}`}
                      onClick={() => setPromptTab(category.id)}
                    >
                      <span aria-hidden="true">{category.icon}</span>
                      {category.label}
                      {category.isNew && <span className="ai-chatbot__tab-dot" title="New" aria-hidden="true" />}
                    </button>
                  ))}
                </div>
                <div className="ai-chatbot__prompts-grid" role="tabpanel" key={activeCategory.id}>
                  {activeCategory.prompts.slice(0, 6).map((prompt) => (
                    <button
                      key={prompt.title}
                      className="ai-chatbot__prompt-card"
                      onClick={() => handlePromptClick(prompt.prompt)}
                      disabled={isTyping}
                      title={prompt.prompt}
                    >
                      <span className="ai-chatbot__prompt-head">
                        <span className="ai-chatbot__prompt-icon" aria-hidden="true">{prompt.icon}</span>
                        <span className="ai-chatbot__prompt-title">{prompt.title}</span>
                      </span>
                      <span className="ai-chatbot__prompt-text">{prompt.prompt}</span>
                    </button>
                  ))}
                </div>
                <p className="ai-chatbot__welcome-note">Chats are saved, so you can come back to them later.</p>
                <p className="ai-chatbot__shortcuts">
                  <kbd>/</kbd> focus · <kbd>Ctrl</kbd>+<kbd>K</kbd> new chat · <kbd>Esc</kbd> stop
                </p>
              </div>
            ) : (
              <div
                className="ai-chatbot__message-list"
                role="log"
                aria-live="polite"
                aria-busy={isTyping}
              >
                {messages.map((msg) => {
                  const isAi = msg.role === "ai";
                  const isStreaming = msg.id === streamingId;
                  const showActions = isAi && !isStreaming && msg.content;
                  const showFollowUps =
                    isAi && msg.id === lastAiId && !isTyping && msg.followUps?.length > 0 && !msg.status;
                  return (
                    <div key={msg.id} className={`ai-chatbot__message ai-chatbot__message--${msg.role}`}>
                      {isAi && (
                        <div className="ai-chatbot__avatar">
                          <img src={tiger0} alt="" className="sidebar-header__nav-icon" />
                        </div>
                      )}
                      <div className="ai-chatbot__bubble-wrap">
                        <div
                          className={`ai-chatbot__bubble ai-chatbot__bubble--${msg.role}${msg.status === "error" ? " ai-chatbot__bubble--error" : ""}`}
                        >
                          <div className="ai-chatbot__bubble-content">
                            {isAi ? renderMarkdown(msg.content) : msg.content}
                            {msg.status === "stopped" && (
                              <span className="ai-chatbot__stopped-tag">Stopped</span>
                            )}
                          </div>
                          {isAi && !isStreaming && msg.sources && <SourcesRow sources={msg.sources} />}
                        </div>

                        <div className="ai-chatbot__meta-row">
                          <span className="ai-chatbot__ts">{formatTime(msg.ts)}</span>
                          {showActions && (
                            <div className="ai-chatbot__msg-actions">
                              <button
                                type="button"
                                onClick={() => copyAnswer(msg)}
                                aria-label="Copy answer"
                                title={copiedId === msg.id ? "Copied" : "Copy"}
                              >
                                {copiedId === msg.id ? (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                ) : (
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                                    <path d="M5 15V5a2 2 0 0 1 2-2h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                  </svg>
                                )}
                              </button>
                              {msg.serverId && (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => rateAnswer(msg, 1)}
                                    aria-label="Helpful"
                                    aria-pressed={msg.feedback === 1}
                                    className={msg.feedback === 1 ? "ai-chatbot__msg-action--on" : ""}
                                    title="Helpful"
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill={msg.feedback === 1 ? "currentColor" : "none"} aria-hidden="true">
                                      <path d="M7 10v11H3V10h4zm0 0l4-7a2 2 0 0 1 2 2v4h6a2 2 0 0 1 2 2.3l-1.4 7A2 2 0 0 1 17.6 20H7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => rateAnswer(msg, -1)}
                                    aria-label="Not helpful"
                                    aria-pressed={msg.feedback === -1}
                                    className={msg.feedback === -1 ? "ai-chatbot__msg-action--on" : ""}
                                    title="Not helpful"
                                  >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill={msg.feedback === -1 ? "currentColor" : "none"} aria-hidden="true">
                                      <path d="M17 14V3h4v11h-4zm0 0l-4 7a2 2 0 0 1-2-2v-4H5a2 2 0 0 1-2-2.3l1.4-7A2 2 0 0 1 6.4 4H17" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    </svg>
                                  </button>
                                </>
                              )}
                              {(msg.status || msg.id === lastAiId) && msg.question && (
                                <button
                                  type="button"
                                  onClick={() => sendMessage(msg.question)}
                                  disabled={isTyping}
                                  aria-label="Ask again"
                                  title="Ask again"
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8M21 3v5h-5M21 12a9 9 0 0 1-15.5 6.2L3 16m0 5v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>

                        {noteDraft?.id === msg.id && (
                          <form
                            className="ai-chatbot__note-form"
                            onSubmit={(e) => {
                              e.preventDefault();
                              submitNote(msg);
                            }}
                          >
                            <input
                              autoFocus
                              value={noteDraft.text}
                              maxLength={500}
                              placeholder="What was wrong? (optional)"
                              aria-label="What was wrong with this answer"
                              onChange={(e) => setNoteDraft({ id: msg.id, text: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") {
                                  e.stopPropagation();
                                  setNoteDraft(null);
                                }
                              }}
                            />
                            <button type="submit">Send</button>
                          </form>
                        )}

                        {showFollowUps && (
                          <div className="ai-chatbot__followups" aria-label="Suggested follow-up questions">
                            {msg.followUps.map((q) => (
                              <button key={q} type="button" onClick={() => sendMessage(q)}>
                                {q}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {isTyping && !streamingId && (
                  <div className="ai-chatbot__message ai-chatbot__message--ai">
                    <div className="ai-chatbot__avatar">
                      <img src={tiger0} alt="" className="sidebar-header__nav-icon" />
                    </div>
                    <div className="ai-chatbot__bubble-wrap">
                      <div className="ai-chatbot__bubble ai-chatbot__bubble--ai">
                        <ol className="ai-chatbot__steps" aria-live="polite">
                          {steps.map((step, i) => {
                            const current = i === steps.length - 1;
                            return (
                              <li key={`${i}-${step}`} className={current ? "ai-chatbot__step--current" : "ai-chatbot__step--done"}>
                                {current ? (
                                  <span className="ai-chatbot__step-dot" aria-hidden="true" />
                                ) : (
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                  </svg>
                                )}
                                {step}
                              </li>
                            );
                          })}
                        </ol>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {showJump && (
            <button type="button" className="ai-chatbot__jump" onClick={() => scrollToBottom()}>
              ↓ New answer
            </button>
          )}

          <div className="ai-chatbot__input-area">
            <div className="ai-chatbot__input-wrap">
              <textarea
                ref={inputRef}
                className="ai-chatbot__input"
                placeholder={isTyping ? "Type your next question… (Esc to stop)" : "Ask anything about operations…"}
                aria-label="Ask Tiger X AI"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                rows={1}
                disabled={threadLoading}
              />
              {isTyping ? (
                <button
                  className="ai-chatbot__send-btn ai-chatbot__stop-btn"
                  onClick={stopAnswer}
                  aria-label="Stop answering"
                  title="Stop (Esc)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
                  </svg>
                </button>
              ) : (
                <button
                  className={`ai-chatbot__send-btn${!inputValue.trim() || threadLoading ? " ai-chatbot__send-btn--off" : ""}`}
                  onClick={handleSend}
                  disabled={!inputValue.trim() || threadLoading}
                  aria-label="Send message"
                  title="Send message"
                >
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {deleteTarget && (
        <div className="ai-chatbot__dialog-backdrop" onClick={() => !deleting && setDeleteTarget(null)}>
          <div
            className="ai-chatbot__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ai-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="ai-delete-title">Delete this chat?</h3>
            <p>
              <strong>{deleteTarget.title || "New chat"}</strong> and its messages will be removed from your history.
            </p>
            <div className="ai-chatbot__dialog-actions">
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="ai-chatbot__dialog-danger" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AiChatbot;
